import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from 'vnetwork-ui'

import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

import FileIcons from '@/icons/FileIcons'
import LoadIcons from '@/icons/LoadIcons'
import WorkDayIcons from '@/icons/WorkDayIcons'
import { useWorkdayList } from '../../../hooks/use-workday-list'
import { syncWorkdayFile } from '../../../services'
import AutoSyncDialog from '../AutoSyncDialog'
import ExportWorkDayDialog, { ExportWorkDayTrigger } from '../ExportButton'

const WorkDayList = () => {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isAutoSyncDialogOpen, setIsAutoSyncDialogOpen] = useState(false)
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)

    const {
        workdays,
        pagination,
        stateComponent,
        control,
        setFilter,
        params,
        searchInputProps,
        columns,
        actions,
        filterItems,
        filterToggles,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        refetch,
    } = useWorkdayList()

    const { mutateAsync: syncWorkday, isPending } = useMutationWithHandler(
        {
            mutationFn: syncWorkdayFile,
        },
        {
            reactQueryKeys: QUERY_KEYS.WORKDAYS,
            successMessage: t('sync-success'),
            onSuccess: () => {
                refetch()
            },
        }
    )

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (event.target.files?.length) {
            const file = event.target.files[0]
            await syncWorkday(file)
            event.target.value = ''
        }
    }

    return (
        <div className="space-y-2">
            {/* Action Buttons */}
            <Paper className="flex flex-wrap gap-2 justify-end py-2">
                <div key="sync-upload">
                    <input
                        type="file"
                        ref={inputRef}
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={isPending}
                        loading={isPending}
                        startIcon={<FileIcons />}
                        onClick={() => inputRef.current?.click()}
                    >
                        {t('sync-data')}
                    </Button>
                </div>

                <div key="auto-sync">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LoadIcons className="w-4 h-4" />}
                        onClick={() => setIsAutoSyncDialogOpen(true)}
                    >
                        {t('auto-sync')}
                    </Button>
                </div>

                <Link key="holiday-link" to="/workdays/holiday">
                    <Button
                        size="small"
                        variant="outlined"
                        type="button"
                        startIcon={<WorkDayIcons />}
                    >
                        {t('holiday-manage')}
                    </Button>
                </Link>
            </Paper>

            <Paper className="space-y-2">
                <FilterBar
                    storageKey="workday-list"
                    control={control}
                    setFilter={setFilter}
                    hideSearch
                    items={filterItems}
                />

                {stateComponent || (
                    <DataTableCustomized
                        columns={columns}
                        rows={workdays}
                        rowKey={(row) =>
                            `${row.employee_id}-${row.work_day_date}`
                        }
                        rowClassName={(row) =>
                            !row.work_day_id ? 'bg-gray-200' : ''
                        }
                        resizable
                        size="small"
                        autoWidth
                        actions={actions}
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('business-search-placeholder', {
                                    defaultValue: 'Tìm kiếm...',
                                }),
                            },
                            settings: {
                                show: true,
                                storageKey: 'workday-list',
                                filterToggles,
                                actionComponents: [
                                    <ExportWorkDayTrigger
                                        key="export"
                                        onOpen={() =>
                                            setIsExportDialogOpen(true)
                                        }
                                    />,
                                ],
                            },
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                        }}
                    />
                )}
            </Paper>

            <AutoSyncDialog
                open={isAutoSyncDialogOpen}
                onClose={() => setIsAutoSyncDialogOpen(false)}
                refetchData={refetch}
            />

            <ExportWorkDayDialog
                open={isExportDialogOpen}
                onClose={() => setIsExportDialogOpen(false)}
            />
        </div>
    )
}

export default WorkDayList
